import React, { useEffect, useState, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  Fade,
  Button
} from '@mui/material';
import {
  Close,
  CheckCircle,
  RadioButtonUnchecked,
  Notifications,
  Snooze,
  NotificationsOff
} from '@mui/icons-material';
import moment from 'moment';
import { Notice } from '../../resources/types/noticesTypes';
import { useSetNoticeViewedMutation } from '../../redux/api/noticesApi';
import { muteNotificationsFor30Minutes, setNotificationsEnabled } from '../../utils/notificationsSettings';

interface NotificationBoxProps {
  notices: Notice[];
  onClose?: () => void;
  onRefresh?: () => void;
}

const NotificationContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  width: 380,
  height: 500,
  maxHeight: '80vh',
  minHeight: 200,
  maxWidth: '90vw',
  minWidth: 320,
  zIndex: 10000,
  borderRadius: 12,
  overflow: 'hidden',
  resize: 'both',
  boxShadow: theme.palette.mode === 'light'
    ? '0 8px 32px rgba(0, 0, 0, 0.12)'
    : '0 8px 32px rgba(0, 0, 0, 0.6)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  display: 'flex',
  flexDirection: 'column'
}));

const Header = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const NoticesList = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 12
});

const NoticeItem = styled(Box)(({ theme }) => ({
  padding: 12,
  marginBottom: 8,
  borderRadius: 8,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#3A3A3A',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? '#EEEEEE' : '#454545'
  }
}));

const getNoticeTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    largeDistant: 'Велика відстань',
    outsideGreenZone: 'Поза зеленою зоною',
    scooterDisconect: 'Відключення',
    usual: 'Звичайне'
  };
  return typeLabels[type] || type;
};

const getNoticeTypeColor = (type: string): string => {
  const typeColors: Record<string, string> = {
    largeDistant: '#FF5722',
    outsideGreenZone: '#FF9800',
    scooterDisconect: '#F44336',
    usual: '#2196F3'
  };
  return typeColors[type] || '#9E9E9E';
};

const sanitizeNoticeText = (text: string): string => {
  // Allow only safe formatting tags without any attributes
  const allowedTags = ['b', 'i', 'br', 'strong', 'em'];
  const tagPattern = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
  
  return text.replace(tagPattern, (match, tagName) => {
    const lowerTagName = tagName.toLowerCase();
    if (allowedTags.includes(lowerTagName)) {
      // Rebuild tag without any attributes to prevent XSS
      const isClosing = match.startsWith('</');
      return isClosing ? `</${lowerTagName}>` : `<${lowerTagName}>`;
    }
    // Remove disallowed tags but keep their content
    return '';
  });
};

const NotificationBox: React.FC<NotificationBoxProps> = ({ notices, onClose, onRefresh }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [setNoticeViewed] = useSetNoticeViewedMutation();
  const [readNotices, setReadNotices] = useState<Set<string>>(new Set());
  const [displayCount, setDisplayCount] = useState(10);
  const [position, setPosition] = useState<{ top: number | string; left: number | string; bottom: number | string; right: number | string }>({ 
    top: 'auto', 
    left: 'auto', 
    bottom: 20, 
    right: 20 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setReadNotices(new Set());
    setDisplayCount(10);
  }, [notices]);

  const handleMarkAsRead = async (noticeId: string) => {
    setReadNotices(prev => {
      const newSet = new Set(prev);
      newSet.add(noticeId);
      return newSet;
    });
    
    try {
      await setNoticeViewed({ id: noticeId, viewed: true }).unwrap();
    } catch (error) {
      console.error('Error marking notice as read:', error);
      setReadNotices(prev => {
        const newSet = new Set(prev);
        newSet.delete(noticeId);
        return newSet;
      });
    }
  };

  const handleScroll = () => {
    if (!listRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
    
    if (isNearBottom && displayCount < notices.length) {
      setDisplayCount(prev => Math.min(prev + 10, notices.length));
    }
  };

  const handleShowAll = () => {
    window.open('/notices-2', '_blank');
  };

  const handleMute30Min = () => {
    muteNotificationsFor30Minutes();
    if (onRefresh) onRefresh();
  };

  const handleDisableNotifications = () => {
    setNotificationsEnabled(false);
    if (onRefresh) onRefresh();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    
    setPosition({
      top: rect.top,
      left: rect.left,
      bottom: 'auto',
      right: 'auto'
    });
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const newLeft = e.clientX - dragStart.x;
      const newTop = e.clientY - dragStart.y;

      setPosition({
        top: newTop,
        left: newLeft,
        bottom: 'auto' as any,
        right: 'auto' as any
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (notices.length === 0) {
    return null;
  }

  const displayedNotices = notices.slice(0, displayCount);
  const hasMoreNotices = notices.length > displayCount;

  return (
    <Fade in timeout={300}>
      <NotificationContainer
        ref={containerRef}
        sx={{
          top: position.top,
          left: position.left,
          bottom: position.bottom,
          right: position.right,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <Header
          onMouseDown={handleMouseDown}
          sx={{ cursor: 'grab', userSelect: 'none' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Notifications />
            <Typography variant="subtitle1" fontWeight={600}>
              Нові сповіщення
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip 
              title="Вимкнути на 30 хв" 
              arrow
              enterDelay={300}
              PopperProps={{
                style: { zIndex: 10001 }
              }}
            >
              <IconButton
                size="small"
                onClick={handleMute30Min}
                sx={{ color: 'inherit' }}
              >
                <Snooze fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip 
              title="Вимкнути сповіщення"
              arrow
              enterDelay={300}
              PopperProps={{
                style: { zIndex: 10001 }
              }}
            >
              <IconButton
                size="small"
                onClick={handleDisableNotifications}
                sx={{ color: 'inherit' }}
              >
                <NotificationsOff fontSize="small" />
              </IconButton>
            </Tooltip>
            {onClose && (
              <Tooltip 
                title="Закрити"
                arrow
                enterDelay={300}
                PopperProps={{
                  style: { zIndex: 10001 }
                }}
              >
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{ color: 'inherit' }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Header>

        <NoticesList ref={listRef} onScroll={handleScroll}>
          {displayedNotices.map((notice) => {
            const isRead = readNotices.has(notice._id);
            return (
              <NoticeItem 
                key={notice._id}
                sx={{
                  opacity: isRead ? 0.6 : 1,
                  transition: 'opacity 0.3s ease'
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Chip
                    label={getNoticeTypeLabel(notice.type)}
                    size="small"
                    sx={{
                      backgroundColor: getNoticeTypeColor(notice.type),
                      color: '#FFFFFF',
                      fontWeight: 500
                    }}
                  />
                  <Tooltip title={isRead ? 'Прочитано' : 'Позначити як прочитане'}>
                    <IconButton
                      size="small"
                      onClick={() => handleMarkAsRead(notice._id)}
                      disabled={isRead}
                      sx={{
                        ml: 1,
                        backgroundColor: isRead 
                          ? (theme.palette.mode === 'light' ? '#E8F5E9' : 'rgba(76, 175, 80, 0.12)')
                          : (theme.palette.mode === 'light' ? '#F5F5F5' : 'rgba(255, 255, 255, 0.08)'),
                        color: isRead ? '#4CAF50' : 'text.secondary',
                        '&:hover': {
                          backgroundColor: isRead 
                            ? (theme.palette.mode === 'light' ? '#C8E6C9' : 'rgba(76, 175, 80, 0.2)')
                            : (theme.palette.mode === 'light' ? '#E0E0E0' : 'rgba(255, 255, 255, 0.12)')
                        },
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.mode === 'light' ? '#E8F5E9' : 'rgba(76, 175, 80, 0.12)',
                          color: '#4CAF50'
                        }
                      }}
                    >
                      {isRead ? <CheckCircle fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
                
                <Typography 
                  variant="body2" 
                  sx={{ wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: sanitizeNoticeText(notice.text) }}
                />
                
                <Typography variant="caption" color="text.secondary">
                  {notice.createdAt ? moment(notice.createdAt).format('HH:mm') : ''}
                </Typography>
              </NoticeItem>
            );
          })}
          
          {!hasMoreNotices && notices.length > 10 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Button
                onClick={handleShowAll}
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Показати всі
              </Button>
            </Box>
          )}
        </NoticesList>
      </NotificationContainer>
    </Fade>
  );
};

export default NotificationBox;
